// OrganisationAssigning — Admin tab for assigning admin users to organisations

import {
  AlertCircle,
  Building2,
  CheckCircle,
  Loader2,
  Mail,
  Search,
  UserPlus,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { supabase } from "../../utils/supabaseClient";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

type OrgAdmin = {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
};

type OrganisationWithAdmin = {
  id: string;
  name: string;
  description: string | null;
  tenant_type: string;
  created_at: string;
  admins: OrgAdmin[];
};

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("No active session");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.access_token}`,
  };
};

export const OrganisationAssigning: React.FC = () => {
  const [organisations, setOrganisations] = useState<OrganisationWithAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<OrganisationWithAdmin | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const fetchOrganisations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/admin/org-assigning`, { headers });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `Server responded with ${response.status}`);
      }

      const data = await response.json();
      setOrganisations(data.data || []);
    } catch (err) {
      console.error("Failed to fetch organisations:", err);
      setError(err instanceof Error ? err.message : "Failed to load organisations");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganisations();
  }, [fetchOrganisations]);

  const handleAssignAdmin = async () => {
    if (!selectedOrg || !inviteEmail.trim()) return;

    setIsSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/admin/org-assigning/assign`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email: inviteEmail.trim(),
          organisation_id: selectedOrg.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Server responded with ${response.status}`);
      }

      if (data.action === "assigned") {
        toast({
          title: "Admin Assigned",
          description: `${data.user?.name || data.user?.email} has been assigned as admin. Their default workspace has been switched.`,
        });
      } else if (data.action === "invited") {
        toast({
          title: "Invitation Sent",
          description: `Invitation sent to ${data.email}. After they accept, return here to assign admin role.`,
        });
      }

      setSelectedOrg(null);
      setInviteEmail("");
      fetchOrganisations();
    } catch (err) {
      console.error("Failed to assign admin:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to assign admin.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredOrgs = searchTerm
    ? organisations.filter((org) =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : organisations;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Organisation Assigning</h1>
        <p className="text-muted-foreground mt-1">
          Assign admin users to organisations. If the user hasn't signed up yet,
          an invitation email will be sent.
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search organisations by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
            <Button variant="outline" onClick={fetchOrganisations} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Organisation Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Organisations ({filteredOrgs.length})
          </CardTitle>
          <CardDescription>
            View all organisations and their assigned admins.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading organisations...</span>
            </div>
          ) : filteredOrgs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm
                ? "No organisations match your search."
                : "No organisations found. Use the Organisation Seeding tab to create some."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Current Admin</TableHead>
                  <TableHead className="w-40">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrgs.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{org.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {org.id.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {org.description || "—"}
                    </TableCell>
                    <TableCell>
                      {org.admins.length > 0 ? (
                        <div className="space-y-1">
                          {org.admins.map((admin) => (
                            <div key={admin.user_id} className="flex items-center gap-2">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              <span className="text-sm">
                                {admin.full_name || "Unknown"}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                <Mail className="h-3 w-3 mr-1" />
                                {admin.email}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          No admin assigned
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedOrg(org);
                          setInviteEmail("");
                        }}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Invite Admin
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assign Admin Dialog */}
      <Dialog
        open={!!selectedOrg}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedOrg(null);
            setInviteEmail("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Admin</DialogTitle>
            <DialogDescription>
              Assign an admin to{" "}
              <strong>{selectedOrg?.name}</strong>. If the user exists, they'll
              be assigned immediately and their workspace switched. If not, an
              invitation email will be sent.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                Email Address
              </label>
              <Input
                type="email"
                placeholder="admin@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && inviteEmail.trim()) {
                    handleAssignAdmin();
                  }
                }}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedOrg(null);
                  setInviteEmail("");
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignAdmin}
                disabled={!inviteEmail.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Assign Admin
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
